/**
 * DisclaimerBlock — the ALWAYS-ON legal/safety disclaimer (AIX-4, governed wording). Rendered on every
 * state of the panel — before generation, alongside a generated explanation, and on the graceful
 * "indisponible" state — so the disclaimer is never missing from the surface.
 *
 * The wording is config-GOVERNED on the server and rides every generate/regenerate result as
 * `disclaimer` (a bilingual "FR\nAR" string); when no generation has run yet we fall back to the i18n
 * keys (which mirror the governed wording verbatim). Either way both the FR and the AR line are shown,
 * each in its own `dir` so the Arabic line reads RTL even under a French (LTR) shell and vice-versa.
 *
 * Uses a warn-tinted Card with a glyph (⚠) + text — never color alone (daltonisme + médico-légal).
 */
import { useT } from "@/app";

export interface DisclaimerBlockProps {
  /**
   * The governed bilingual disclaimer returned by the server (FR newline AR). When absent (no
   * generation yet) the component falls back to the i18n FR/AR keys.
   */
  serverDisclaimer?: string | null;
}

/** Split the server's "FR\nAR" bilingual disclaimer into its two governed lines. */
function splitBilingual(text: string): { fr: string; ar: string } {
  const parts = text.split("\n").map((s) => s.trim()).filter(Boolean);
  return { fr: parts[0] ?? text, ar: parts[1] ?? "" };
}

export function DisclaimerBlock({ serverDisclaimer }: DisclaimerBlockProps) {
  const { t } = useT();
  const fromServer = serverDisclaimer ? splitBilingual(serverDisclaimer) : null;
  const fr = fromServer?.fr || t("aix.disclaimer.fr");
  const ar = fromServer?.ar || t("aix.disclaimer.ar");

  return (
    <aside
      role="note"
      aria-label={t("aix.disclaimer.title")}
      style={{
        display: "flex",
        gap: "var(--nv-s-3)",
        padding: "var(--nv-s-4)",
        borderRadius: "var(--nv-r-md)",
        background: "var(--nv-tint-high)",
        border: "1px solid var(--nv-line-faint)",
        borderInlineStart: "3px solid var(--nv-warn)",
      }}
    >
      <span aria-hidden style={{ color: "var(--nv-warn)", fontSize: "var(--nv-fs-body-lg)", lineHeight: 1 }}>
        ⚠
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)", minWidth: 0 }}>
        <span
          style={{
            fontSize: "var(--nv-fs-label)",
            fontWeight: 600,
            color: "var(--nv-warn)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {t("aix.disclaimer.title")}
        </span>
        <p lang="fr" dir="ltr" style={{ margin: 0, color: "var(--nv-graphite)", fontSize: "var(--nv-fs-caption)" }}>
          {fr}
        </p>
        {ar && (
          <p lang="ar" dir="rtl" style={{ margin: 0, color: "var(--nv-graphite)", fontSize: "var(--nv-fs-caption)" }}>
            {ar}
          </p>
        )}
      </div>
    </aside>
  );
}
